import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { CameraWithInfo, Solar4gCamera } from '@EcamModel/model';
import { PTZCamera } from '@EcamModel/model/PTZCamera';
import { SP111Camera } from '@EcamModel/model/SP111Cameras';
import { Box, Typography } from '@mui/material';
import { formatDateByTimeZone, formatUKDate, formatUKDateTime } from 'src/helpers';

interface CameraDetailsProps {
    cameraType: CameraType;
    cameraItem: CameraWithInfo;
}

export default function CameraItemInfoDetails({ cameraItem, cameraType }: CameraDetailsProps) {
    const cameraData: Solar4gCamera | PTZCamera | SP111Camera | undefined = {
        [CameraType.Solar]: cameraItem.Solar4gCamera,
        [CameraType.PTZ]: cameraItem.PTZCamera,
        [CameraType.SP111]: cameraItem.SP111Camera,
        [CameraType.PMC8266]: cameraItem.PMC8266Camera,
    }[cameraType];

    const detailItems: { label: string; value: string }[] = [];

    // Add MAC if available
    if (cameraData?.Mac) {
        detailItems.push({ label: 'MAC', value: cameraData.Mac });
    }

    // Add IP Address if available
    if (cameraItem.IP) {
        detailItems.push({ label: 'IP Address', value: cameraItem.IP });
    }

    // Add Lens Capacity if available
    if (cameraItem.LensCapacity) {
        detailItems.push({ label: 'Len caps', value: cameraItem.LensCapacity });
    }

    // Add Production date
    detailItems.push({ label: 'Production', value: formatUKDate(cameraItem.DeployedDate) });

    // Add Latest photo for Solar cameras
    if (cameraType === CameraType.Solar && cameraItem.CameraPhotos?.[0]?.CaptureAt) {
        detailItems.push({
            label: 'Latest photo',
            value: cameraItem.CameraPhotos?.[0]?.CaptureAt
                ? formatDateByTimeZone(cameraItem.CameraPhotos?.[0]?.CaptureAt, cameraItem.TimeZone)
                : 'Not available',
        });
    }

    const lastContact =
        cameraType === CameraType.Solar
            ? cameraItem.Solar4gCamera?.LastContact
            : cameraType === CameraType.PTZ
              ? cameraItem.PTZCamera?.LastContact
              : undefined;

    if (lastContact) {
        detailItems.push({ label: 'Last contact', value: formatUKDateTime(lastContact) });
    }

    // Add Updated
    detailItems.push({ label: 'Updated', value: formatUKDateTime(cameraItem.Updated) });

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${detailItems.length}, 1fr)`,
                gap: 2,
                width: '100%',
            }}
        >
            {detailItems.map((item, index) => (
                <Box key={index} sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
                        {item.label}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>
                        {item.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
